import React, { useState } from "react"

import cn from "classnames"
import { SearchState } from "react-instantsearch-core"
import { SearchBox } from "react-instantsearch-dom"
import { useIntl } from "react-intl"

import Tooltip from "../../ui/Tooltip/Tooltip"
import CustomClearRefinements from "../Algolia/CustomClearRefinments/CustomClearRefinments"
import CustomRefinmentList from "../Algolia/CustomRefinmentList/CustomRefinmentList"
import CustomRefinmentListDiet from "../Algolia/CustomRefinmentListDiet/CustomRefinmentListDiet"
import CustomRefinmentListDifficulty from "../Algolia/CustomRefinmentListDifficulty/CustomRefinmentListDifficulty"
import CustomRefinmentListType from "../Algolia/CustomRefinmentListType/CustomRefinmentListType"
import CustomHierarchicalMenu from "../Algolia/HierarchicalMenu/CustomHierarchicalMenu"
import HitCounter from "../Algolia/HitsCounter/HitsCounter"
import filters from "../assets/filters.svg"
import DropdownFilters from "../DropdownFilters/DropdownFilters"
import FilterCounter from "../FilterCounter/FilterCounter"
import Results from "../Results"
import { getFilterNumber } from "../utils/getFilterNumber"
import StaredRefinmentList from "~/components/FilteredRecipes/Algolia/DoubleRefinementList/StaredRefinmentList"
import Separator from "~/components/Separator/Separator"
import { Button, ColumnCount } from "~/components/ui"
import { Col, Row } from "~/components/ui/Grid/Grid"
import { Body, Heading } from "~/components/ui/Typography/Typography"
import useCategories from "~/hooks/useCategories"
import useIsClient from "~/hooks/useIsClient"
import { RecipeAlgoliaHit } from "~/models/Recipe"
import { ResultState } from "~/templates/Category/types"
import { recursiveCategoriesRenaming } from "~/utils/createPages/categoriesUtils"
import { ResponsiveValues } from "~/utils/responsiveCN"

import css from "./FilteredRecipesList.module.scss"

const GATSBY_ALGOLIA_INDEX = process.env.GATSBY_ALGOLIA_INDEX ?? ""
const [TOOLTIP_APPEARING_DELAY, TOOLTIP_DISAPPEARING_DELAY] = [1, 3] // in seconds

type PropTypes = {
  baseResultState: ResultState<RecipeAlgoliaHit>
  baseHierarchicalFacetsItems: any
  baseVerticalRefinementListItems: any
  baseStarIngredientsRefinementListItems: any
  searchState: SearchState
  setSearchState: (state: SearchState) => void
  setNbHits: (nbHits: number) => void
}

function FilteredRecipesList({
  baseResultState,
  baseHierarchicalFacetsItems,
  baseVerticalRefinementListItems,
  baseStarIngredientsRefinementListItems,
  searchState,
  setSearchState,
  setNbHits,
}: PropTypes) {
  const [showFiltersInMobile, setShowFiltersInMobile] = useState(false)
  const [initialSearchState, setInitialSearchState] = useState(searchState)

  return (
    <Row className={css.algoliaContent}>
      <ListHeader
        width={[12, 12]}
        setShowFiltersInMobile={setShowFiltersInMobile}
        showFiltersInMobile={showFiltersInMobile}
        searchState={searchState}
        setInitialSearchState={setInitialSearchState}
        baseResultState={baseResultState}
        setNbHits={setNbHits}
      />

      <FiltersColumn
        width={[showFiltersInMobile ? 12 : 0, showFiltersInMobile ? 0 : 3]}
        setShowFiltersInMobile={setShowFiltersInMobile}
        initialSearchState={initialSearchState}
        setSearchState={setSearchState}
        baseHierarchicalFacetsItems={baseHierarchicalFacetsItems}
        baseVerticalRefinementListItems={baseVerticalRefinementListItems}
        baseStarIngredientsRefinementListItems={
          baseStarIngredientsRefinementListItems
        }
        searchState={searchState}
      />

      {
        //Results
      }
      <Col
        width={[showFiltersInMobile ? 0 : 12, showFiltersInMobile ? 12 : 9]}
        className={showFiltersInMobile ? "" : css.resultsContent}
      >
        <Results
          indexName={GATSBY_ALGOLIA_INDEX}
          resultState={baseResultState}
          isFilterDisplayed={showFiltersInMobile}
        />
      </Col>
      <div style={{ display: "none" }}>
        <SearchBox />
      </div>
    </Row>
  )
}

const ListHeader = ({
  width,
  showFiltersInMobile,
  setShowFiltersInMobile,
  searchState,
  setInitialSearchState,
  baseResultState,
  setNbHits,
}: {
  width: ResponsiveValues<ColumnCount>
  showFiltersInMobile: boolean
  setShowFiltersInMobile: (value: boolean) => void
  searchState: SearchState
  setInitialSearchState: (state: SearchState) => void
  baseResultState: ResultState<RecipeAlgoliaHit>
  setNbHits: (nbHits: number) => void
}) => {
  const intl = useIntl()

  function handleClickFilters() {
    if (!showFiltersInMobile) {
      setInitialSearchState(searchState)
    }
    setShowFiltersInMobile(!showFiltersInMobile)
  }

  const isClient = useIsClient()
  return (
    <Col width={width} className={css.activateFilters}>
      <Body
        variant="body1"
        bold
        onClick={handleClickFilters}
        className={css.regularActivation}
      >
        {showFiltersInMobile
          ? intl.formatMessage({
              id: "category/text:show-filters",
            })
          : intl.formatMessage({
              id: "category/text:hide-filters",
            })}
        <img className={css.filters} src={filters} alt="" />
      </Body>

      <HitCounter resultState={baseResultState} setNbHits={setNbHits} />

      <Button
        onClick={handleClickFilters}
        className={css.compactActivation}
        variant="secondary"
        size="small"
        uppercase={false}
        color="#BDBDBD"
      >
        <Body variant="body1" bold color="black">
          {intl.formatMessage({
            id: "category/text:filters",
          })}
        </Body>

        <Tooltip
          appearingDelay={TOOLTIP_APPEARING_DELAY}
          disappearingDelay={TOOLTIP_DISAPPEARING_DELAY}
          top={14}
          showOnce
          id={"filtered-recipes"}
        >
          <Body variant="body2" semiBold color="white">
            {intl.formatMessage({
              id: "category/filters/text:tooltip-content",
            })}
          </Body>
        </Tooltip>
        <img className={css.filters} src={filters} alt="" />
        {!!isClient && (
          <FilterCounter
            count={getFilterNumber({ searchState: searchState })}
            color="var(--original-color)"
          />
        )}
      </Button>
    </Col>
  )
}

const FiltersColumn = ({
  width,
  setShowFiltersInMobile,
  initialSearchState,
  setSearchState,
  baseHierarchicalFacetsItems,
  baseVerticalRefinementListItems,
  baseStarIngredientsRefinementListItems,
  searchState,
}: {
  width: ResponsiveValues<ColumnCount>
  setShowFiltersInMobile: (value: boolean) => void
  initialSearchState: SearchState
  setSearchState: (state: SearchState) => void
  baseHierarchicalFacetsItems: any
  baseVerticalRefinementListItems: any
  baseStarIngredientsRefinementListItems: any
  searchState: SearchState
}) => {
  const intl = useIntl()
  const CATEGORIES = useCategories(intl.locale)

  function handleClose() {
    setSearchState(initialSearchState)
    setShowFiltersInMobile(false)
  }

  return (
    <Col width={width}>
      <div className={css.filtersRoot}>
        <div className={css.onlyCompact}>
          <div className={css.header}>
            <Heading variant="large" Tag="p">
              {intl.formatMessage({
                id: "category/text:filters",
              })}
            </Heading>
            <Body
              variant="body2"
              semiBold
              color="original"
              onClick={handleClose}
              className={css.close}
            >
              {intl.formatMessage({
                id: "category/text:filter-close",
              })}
            </Body>
          </div>
          <Separator />
        </div>
        <div className={css.filtersContent}>
          <DropdownFilters
            label={intl.formatMessage({
              id: "category/text:category-label-filters",
            })}
            id="CategoriesFilter"
            display={
              !baseHierarchicalFacetsItems ||
              baseHierarchicalFacetsItems?.items?.filter(
                (item: any) => item.label
              ).length > 0
            }
          >
            <CustomHierarchicalMenu
              showResetButton
              attributes={[
                "categories_tree.lvl0",
                "categories_tree.lvl1",
                "categories_tree.lvl2",
                "categories_tree.lvl3",
              ]}
              limit={100}
              showParentLevel
              transformItems={(items: any) => {
                return recursiveCategoriesRenaming(items, CATEGORIES)
              }}
              baseItems={baseHierarchicalFacetsItems}
            />
          </DropdownFilters>

          <DropdownFilters
            label={intl.formatMessage({
              id: "category/text:univers-label-filters",
            })}
            id="verticalsFilter"
            count={getFilterNumber({
              searchState: searchState,
              refinmentKey: "vertical",
            })}
          >
            <CustomRefinmentList
              attribute="vertical"
              baseItems={baseVerticalRefinementListItems}
            />
          </DropdownFilters>

          <DropdownFilters
            label={intl.formatMessage({
              id: "category/text:ingredients-label-filters",
            })}
            id="CategoriesFilter"
            display={
              !baseStarIngredientsRefinementListItems ||
              baseStarIngredientsRefinementListItems?.length > 0
            }
          >
            <StaredRefinmentList
              attribute="star_ingredients.slug"
              limit={5}
              showMore={true}
              showMoreLimit={1000}
              operator="and"
              baseItems={baseStarIngredientsRefinementListItems}
              category={
                initialSearchState?.hierarchicalFacetsRefinements
                  ?.categories_tree?.length
                  ? CATEGORIES[
                      initialSearchState.hierarchicalFacetsRefinements.categories_tree[0]
                        .split(" > ")
                        .slice(-1)[0]
                    ] ?? undefined
                  : undefined
              }
            />
          </DropdownFilters>

          <DropdownFilters
            label={intl.formatMessage({
              id: "recipe/search/filters/difficulty-level/text:title",
            })}
            id="DifficultyLevelFilter"
            count={getFilterNumber({
              searchState: searchState,
              refinmentKey: "difficulty",
            })}
          >
            <CustomRefinmentListDifficulty attribute="difficulty" />
          </DropdownFilters>
          <DropdownFilters
            label={intl.formatMessage({
              id: "category/text:diet-label-filters",
            })}
            id="DietFilter"
            count={getFilterNumber({
              searchState: searchState,
              refinmentKey: "suitable_for_diets.slug",
            })}
            display={["fr", "en-us"].includes(intl.locale)}
          >
            <CustomRefinmentListDiet
              attribute="suitable_for_diets.slug"
              operator="and"
            />
          </DropdownFilters>
          <DropdownFilters
            label={intl.formatMessage({
              id: "recipe/search/filters/type/text:title",
            })}
            id="RecipeType"
            count={getFilterNumber({
              searchState: searchState,
              refinmentKey: "recipe_type",
            })}
          >
            <CustomRefinmentListType attribute="recipe_type" />
          </DropdownFilters>
        </div>
        <div className={cn(css.onlyCompact, css.CTA)}>
          <div className={css.applyFilters}>
            <Separator />
            <div className={css.cta}>
              <Col width={[6, 6]}>
                <CustomClearRefinements searchState={searchState} />
              </Col>
              <Col width={[6, 6]}>
                <Button
                  color="original"
                  onClick={() => setShowFiltersInMobile(false)}
                >
                  {intl.formatMessage({
                    id: "category/button:apply-filters",
                  })}
                </Button>
              </Col>
            </div>
          </div>
        </div>
      </div>
    </Col>
  )
}

export default FilteredRecipesList
